"use client";

import { useState } from "react";
import { type CampaignAutopilotConfigV2Data } from "@cede/types";
import { PriorityTicketsInfo } from "@/components/features/automatic-streak/PriorityTicketsInfo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PortalModal } from "@/components/ui/portal-modal";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { arenaUrlWithUtm } from "@/constants/airdrop-farming";
import { useAutomaticStreakConfigFormV2, type AutomaticStreakConfigFormV2Data } from "@/hooks/automatic-streak";
import { cn } from "@/lib/utils";
import { formatStreakDuration } from "@/utils/automatic-streak";
import { formatBalance } from "@/utils/hyperliquid/numeric";
import {
  AlertCircle,
  HelpCircle,
  Info,
  CheckCircle2,
  Loader2,
  Minus,
  Plus,
  Repeat,
  Target,
  Timer,
  Calendar,
  ShieldCheck,
  Zap,
} from "lucide-react";

export type { AutomaticStreakConfigFormV2Data };

/** Stable form ID for external button association */
export const AUTOMATIC_STREAK_CONFIG_FORM_V2_ID = "automatic-streak-config-form-v2";

const DEFAULT_SPREAD_THRESHOLD = 2;
const openArena = () => window.open(arenaUrlWithUtm("cedehub-autopilot-queue"), "_blank", "noopener,noreferrer");

export interface AutomaticStreakConfigFormV2Props {
  onSubmit: (data: AutomaticStreakConfigFormV2Data) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  pairSymbol: string;
  accountId: string;
  exchangeId: string;
  campaignName: string;
  epochId: number;
  className?: string;
  config: CampaignAutopilotConfigV2Data;
  epochEndTime?: Date;
  /** Whether a next epoch exists (to show the "Enable for next epoch" option) */
  hasNextEpoch?: boolean;
  hideActions?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  formId?: string;
  /** Spread threshold percentage from campaign config (default: 2%) */
  spreadThreshold?: number | null;
  /** Whether the epoch limit for autopilots is reached (blocks form submission) */
  isEpochLimitReached?: boolean;
  /**
   * Whether the priority-ticket boost field is offered. Only meaningful for
   * queue joins — the direct-create path (queue disabled) has no picking to
   * prioritize.
   */
  showPriorityBoost?: boolean;
}

interface CompactFieldProps {
  label: string;
  children: React.ReactNode;
  error?: string;
  tooltip?: string;
}

const CompactField = ({ label, children, error, tooltip }: CompactFieldProps) => (
  <div className="space-y-1">
    <div className="flex items-center gap-1">
      <Label className="text-cede-text-secondary text-xs">{label}</Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[200px]">{tooltip}</TooltipContent>
        </Tooltip>
      )}
    </div>
    {children}
    {error && (
      <p className="text-xs text-cede-error flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

/**
 * Shared chrome for the two priority-ticket states. The panel is only ~300px
 * wide, so the control gets its own row: sharing one with the title wrapped it
 * to three lines and stranded the tooltip in the gap. The balance rides in a
 * pill beside the title, matching the header chip, which leaves the line below
 * free to say what spending buys.
 */
const PriorityTicketPanel = ({
  isActive,
  detail,
  balance,
  children,
}: {
  isActive: boolean;
  detail: string;
  balance?: number;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      "flex flex-col gap-2 p-3 rounded-lg border transition-all duration-200",
      isActive ? "border-medal-gold/60 bg-medal-gold/10" : "border-medal-gold/25 bg-cede-bg-component",
    )}
  >
    <div className="flex items-center gap-2">
      <Zap
        className={cn("w-4 h-4 shrink-0 text-medal-gold", !isActive && "opacity-60")}
        fill={isActive ? "currentColor" : "none"}
      />
      <span className="truncate font-medium text-sm text-cede-text-primary">Priority tickets</span>
      {balance !== undefined && balance > 0 && (
        <span className="shrink-0 rounded-full border border-medal-gold/35 bg-medal-gold/15 px-2 py-0.5 text-[11px] font-semibold leading-none text-medal-gold tabular-nums">
          {balance}
        </span>
      )}
      <div className="flex-1" />
      <PriorityTicketsInfo compact />
    </div>
    <p className="text-xs text-cede-text-secondary">{detail}</p>
    {children}
  </div>
);

/**
 * V2 Form Component - Target volume configuration (always runs until epoch ends)
 */
export const AutomaticStreakConfigFormV2 = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  pairSymbol,
  accountId,
  exchangeId,
  campaignName,
  epochId,
  className,
  config,
  epochEndTime,
  hasNextEpoch = false,
  hideActions = false,
  onValidityChange,
  formId = AUTOMATIC_STREAK_CONFIG_FORM_V2_ID,
  spreadThreshold,
  isEpochLimitReached = false,
  showPriorityBoost = false,
}: AutomaticStreakConfigFormV2Props) => {
  const displaySpreadThreshold = spreadThreshold ?? DEFAULT_SPREAD_THRESHOLD;
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const {
    fields,
    setters,
    balance,
    calculations,
    validation,
    configBounds,
    hasNextEpoch: hookHasNextEpoch,
    priorityTickets,
    handleSubmit,
  } = useAutomaticStreakConfigFormV2({
    accountId,
    exchangeId,
    pairSymbol,
    campaignName,
    epochId,
    config,
    epochEndTime,
    hasNextEpoch,
    onSubmit,
    onValidityChange,
    isEpochLimitReached,
  });

  const { targetVolume, targetVolumeParsed, alsoRunForNextEpoch, priorityTicketsToSpend } = fields;
  const { setTargetVolume, setAlsoRunForNextEpoch, setPriorityTicketsToSpend } = setters;
  const canBoostWithTickets = showPriorityBoost && priorityTickets.balance > 0;
  // Holding none is the common case, and the queue is exactly where the boost is
  // worth wanting — say it exists rather than rendering nothing. Flag-gated so it
  // stays dark with the rest of the feature.
  // isError matters as much as the count: usePriorityTickets reports 0 on a
  // failed read, so without it a user holding tickets would be told they have
  // none and sent to buy in again.
  const showEarnTicketsPrompt =
    showPriorityBoost &&
    priorityTickets.isFeatureEnabled &&
    !priorityTickets.isLoading &&
    !priorityTickets.isError &&
    priorityTickets.balance === 0;
  const showTicketBalanceError =
    showPriorityBoost && priorityTickets.isFeatureEnabled && !priorityTickets.isLoading && priorityTickets.isError;
  // Live balance can drop below the picked spend while the form is open — never render a negative remainder.
  const ticketsLeftAfterSpend = Math.max(0, priorityTickets.balance - priorityTicketsToSpend);
  const {
    value: balanceValue,
    quoteToken,
    isLoading: isBalanceLoading,
    isMinimumBalanceLoading,
    requiredBalance,
    isSufficient: isBalanceSufficient,
  } = balance;
  const { estimatedDurationSeconds } = calculations;
  const { errors, epochTimeWarning, isFormValid } = validation;

  return (
    <>
      <form id={formId} onSubmit={handleSubmit} className={cn("p-4 space-y-3", className)}>
        {/* Header with How it works */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-cede-text-secondary">{pairSymbol}</p>
          <button
            type="button"
            onClick={() => setShowHowItWorks(true)}
            className="flex items-center gap-1 text-xs text-cede-primary hover:underline"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            How it works?
          </button>
        </div>

        {/* Balance Display */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <span className="text-cede-text-secondary">Available balance</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px]">
                Buffer reserved for fees and price fluctuations.
              </TooltipContent>
            </Tooltip>
          </div>
          {isBalanceLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-cede-text-tertiary" />
          ) : (
            <span className="font-medium text-cede-text-primary">
              {formatBalance(balanceValue)} {quoteToken}
            </span>
          )}
        </div>

        {/* Min Balance Required */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <span className="text-cede-text-secondary">Min balance required</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Minimum balance required to cover trade sizes, spread, and fees. Running multiple autopilots on the
                  same exchange account increases this requirement.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          {isMinimumBalanceLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-cede-text-tertiary" />
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-cede-text-primary">
                {formatBalance(requiredBalance)} {quoteToken}
              </span>
              <span className={cn("flex items-center", isBalanceSufficient ? "text-cede-success" : "text-cede-error")}>
                {isBalanceSufficient ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
              </span>
            </div>
          )}
        </div>

        {/* Min/Max Target Volume Display */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-cede-text-secondary">Min/max volume</span>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-cede-text-primary">
              {configBounds.minTargetVolume}/{configBounds.maxTargetVolume} USD
            </span>
            {errors.targetVolume ? (
              <span className="flex items-center text-cede-error">
                <AlertCircle className="w-3 h-3" />
              </span>
            ) : (
              <span className="flex items-center text-cede-success">
                <CheckCircle2 className="w-3 h-3" />
              </span>
            )}
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-3">
          {/* Target Volume */}
          <CompactField label="Target volume (USD)" tooltip="Total volume to trade until the current epoch ends">
            <div className="relative">
              <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cede-text-tertiary" />
              <Input
                type="number"
                step="1"
                min={configBounds.minTargetVolume}
                max={configBounds.maxTargetVolume}
                value={targetVolume}
                onChange={(e) => setTargetVolume(e.target.value)}
                placeholder={`${configBounds.minTargetVolume}-${configBounds.maxTargetVolume}`}
                className="pl-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </CompactField>

          {/* Enable for next epoch - only shown if next epoch exists and user can have at least 2 active autopilots */}
          {hookHasNextEpoch && config.numberOfAllowedActiveAutopilotPerUser >= 2 && (
            <label
              htmlFor="also-run-next-epoch"
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200",
                "hover:border-cede-primary/30 hover:bg-cede-primary/5",
                alsoRunForNextEpoch
                  ? "border-cede-primary bg-cede-primary/10"
                  : "border-cede-border-container bg-cede-bg-component",
              )}
            >
              <Checkbox
                id="also-run-next-epoch"
                checked={alsoRunForNextEpoch}
                onCheckedChange={(checked) => setAlsoRunForNextEpoch(checked === true)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-cede-primary" />
                  <span className="font-medium text-sm text-cede-text-primary">Enable for next epoch</span>
                </div>
                <p className="text-xs text-cede-text-secondary mt-0.5">
                  Also schedule the same autopilot for the next epoch
                </p>
              </div>
            </label>
          )}

          {canBoostWithTickets && (
            <PriorityTicketPanel
              isActive={priorityTicketsToSpend > 0}
              balance={priorityTickets.balance}
              detail={
                priorityTicketsToSpend > 0
                  ? `Skipping ahead ${priorityTicketsToSpend} epoch${priorityTicketsToSpend > 1 ? "s" : ""} · ${ticketsLeftAfterSpend} left`
                  : "Spend to skip ahead in the queue"
              }
            >
              {/* The earn link fills the space the bare stepper left and keeps a
                  route to Arena for people who already hold a few. */}
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={openArena}
                  className="shrink-0 text-xs text-cede-primary hover:underline"
                >
                  Earn more
                </button>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    aria-label="Spend one less priority ticket"
                    disabled={priorityTicketsToSpend === 0}
                    onClick={() => setPriorityTicketsToSpend(Math.max(0, priorityTicketsToSpend - 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-cede-border-container text-cede-text-secondary transition-colors hover:text-cede-text-primary hover:border-medal-gold disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-medal-gold/60"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span
                    className={cn(
                      "w-6 text-center text-sm font-semibold tabular-nums",
                      priorityTicketsToSpend > 0 ? "text-medal-gold" : "text-cede-text-primary",
                    )}
                  >
                    {priorityTicketsToSpend}
                  </span>
                  <button
                    type="button"
                    aria-label="Spend one more priority ticket"
                    disabled={priorityTicketsToSpend >= priorityTickets.balance}
                    onClick={() =>
                      setPriorityTicketsToSpend(Math.min(priorityTickets.balance, priorityTicketsToSpend + 1))
                    }
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-cede-border-container text-cede-text-secondary transition-colors hover:text-cede-text-primary hover:border-medal-gold disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-medal-gold/60"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </PriorityTicketPanel>
          )}

          {showEarnTicketsPrompt && (
            <PriorityTicketPanel isActive={false} detail="None yet">
              <Button type="button" variant="secondary" size="small" className="w-full" onClick={openArena}>
                Earn in Arena
              </Button>
            </PriorityTicketPanel>
          )}

          {showTicketBalanceError && (
            <PriorityTicketPanel isActive={false} detail="Balance unavailable">
              <p className="text-xs text-cede-error">
                Couldn&apos;t load your priority tickets. You can still join the normal queue.
              </p>
            </PriorityTicketPanel>
          )}

          {/* Epoch runway warning — non-blocking: queue join is still allowed,
              the streak just starts in the next epoch. */}
          {epochTimeWarning && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-cede-warning/10 border border-cede-warning/20">
              <Info className="w-4 h-4 text-cede-warning mt-0.5 shrink-0" />
              <p className="text-xs text-cede-warning">{epochTimeWarning}</p>
            </div>
          )}
        </div>

        {/* Summary Section */}
        <div className="text-xs border-t border-cede-border-container pt-2 space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1 text-cede-text-secondary">
              <Timer className="w-3 h-3" />
              Duration
            </span>
            <span className="text-cede-text-primary">{formatStreakDuration(estimatedDurationSeconds)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1 text-cede-text-secondary">
              <ShieldCheck className="w-3 h-3 text-cede-success" />
              Spread protection
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]">
                  <p>
                    Cycles are automatically skipped when the bid-ask spread exceeds {displaySpreadThreshold}%. This
                    protects you from unfavorable trades during high volatility or low liquidity periods.
                  </p>
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="text-cede-success">{`<${displaySpreadThreshold}%`}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1 text-cede-text-secondary">
              <ShieldCheck className="w-3 h-3 text-cede-success" />
              Volume protection
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]">
                  <p>
                    Autopilot automatically stops when you reach your epoch volume limit. This prevents unnecessary
                    trades and protects you from paying extra fees.
                  </p>
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="text-cede-success">Active</span>
          </div>
        </div>

        {/* Epoch Limit Reached Notice — informational, queue join is still allowed */}
        {isEpochLimitReached && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-cede-warning/10 border border-cede-warning/20">
            <Info className="w-4 h-4 text-cede-warning mt-0.5 shrink-0" />
            <div className="text-xs text-cede-warning">
              <p>
                <strong>Autopilot is at capacity for this epoch.</strong> You can still join the queue — your streak
                will start automatically as soon as a slot opens up.
              </p>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {(!isBalanceSufficient || errors.targetVolume) && !isBalanceLoading && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-cede-error/10 border border-cede-error/20">
            <AlertCircle className="w-4 h-4 text-cede-error mt-0.5 shrink-0" />
            <div className="text-xs text-cede-error space-y-1">
              {!isBalanceSufficient && (
                <p>
                  Your balance ({formatBalance(balanceValue)} {quoteToken}) is below the minimum required (
                  {formatBalance(requiredBalance)} {quoteToken}). Deposit at least{" "}
                  {formatBalance(requiredBalance - balanceValue)} {quoteToken} more to start the autopilot.
                </p>
              )}
              {errors.targetVolume && targetVolumeParsed < configBounds.minTargetVolume && (
                <p>
                  Target volume ({formatBalance(targetVolumeParsed)} {quoteToken}) is below the minimum (
                  {configBounds.minTargetVolume} {quoteToken}). Increase it to at least {configBounds.minTargetVolume}{" "}
                  {quoteToken}.
                </p>
              )}
              {errors.targetVolume && targetVolumeParsed > configBounds.maxTargetVolume && (
                <p>
                  Target volume ({formatBalance(targetVolumeParsed)} {quoteToken}) exceeds the maximum (
                  {configBounds.maxTargetVolume} {quoteToken}). Reduce it to at most {configBounds.maxTargetVolume}{" "}
                  {quoteToken}.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {!hideActions && (
          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="medium"
              disabled={isSubmitting || !isFormValid}
              className="flex-1"
            >
              {isSubmitting ? "Joining queue..." : "Join Autopilot Queue"}
            </Button>
          </div>
        )}
      </form>

      {/* How it works modal - V2 version */}
      <PortalModal open={showHowItWorks} onOpenChange={setShowHowItWorks} className="sm:max-w-[480px]">
        <PortalModal.Header>
          <PortalModal.Title>How Autopilot V2 Works</PortalModal.Title>
          <PortalModal.Description>Automatically executes buy/sell cycles on {pairSymbol}</PortalModal.Description>
        </PortalModal.Header>
        <PortalModal.Content className="space-y-5">
          {/* Visual Timeline */}
          <div className="bg-cede-bg-component rounded-lg p-4 space-y-3">
            <p className="text-xs font-medium text-cede-text-secondary uppercase tracking-wide">How It Works</p>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cede-primary/20 flex items-center justify-center shrink-0">
                <Target className="w-4 h-4 text-cede-primary" />
              </div>
              <div className="pt-1">
                <p className="text-sm font-medium text-cede-text-primary">Set your target volume</p>
                <p className="text-xs text-cede-text-tertiary">
                  The system automatically calculates how many cycles to run
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cede-primary/20 flex items-center justify-center shrink-0">
                <Timer className="w-4 h-4 text-cede-primary" />
              </div>
              <div className="pt-1">
                <p className="text-sm font-medium text-cede-text-primary">Runs until epoch ends</p>
                <p className="text-xs text-cede-text-tertiary">
                  Autopilot automatically runs until the current epoch ends
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cede-success/20 flex items-center justify-center shrink-0">
                <Repeat className="w-4 h-4 text-cede-success" />
              </div>
              <div className="pt-1">
                <p className="text-sm font-medium text-cede-text-primary">Cycles are distributed</p>
                <p className="text-xs text-cede-text-tertiary">
                  Each cycle (buy + sell) is spread evenly across your timeframe
                </p>
              </div>
            </div>
          </div>

          {/* Key Points */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-cede-text-secondary uppercase tracking-wide">Key Points</p>
            <div className="grid gap-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cede-success mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  <strong className="text-cede-text-primary">Volume-based</strong> - set your target, we handle the rest
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cede-success mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  <strong className="text-cede-text-primary">Trade sizes randomized</strong> within{" "}
                  {configBounds.minTradeSize}-{configBounds.maxTradeSize} {quoteToken}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cede-success mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  Ensure enough <strong className="text-cede-text-primary">{quoteToken}</strong> balance
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-cede-warning mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  Small fees and slippage may slightly reduce your balance
                </p>
              </div>
            </div>
          </div>
        </PortalModal.Content>
        <PortalModal.Footer>
          <Button variant="primary" size="medium" onClick={() => setShowHowItWorks(false)} className="w-full">
            Got it
          </Button>
        </PortalModal.Footer>
      </PortalModal>
    </>
  );
};
